// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Button, Typography, Box, CircularProgress, Skeleton } from '@mui/material';
import LightboxModal from '../../../../../components/LightboxModal';
import { doc400 } from '../../../../userImage';

const isHttpUrl = (val) => typeof val === 'string' && /^https?:\/\//i.test(val);
const isPdf = (val) => typeof val === 'string' && /\.pdf(\?|$)/i.test(val);

// `file` may be any of:
//   - null / undefined                 → no file
//   - File / Blob                      → local upload (preview via object URL)
//   - { id, url, mimeType, ... }       → new backend nested-file object
//   - string starting with http(s)://  → legacy or freshly-uploaded URL
//   - anything else                    → legacy storage path → doc400()
const resolvePreviewSrc = (file) => {
  if (!file) return null;
  if (file instanceof File || file instanceof Blob) return URL.createObjectURL(file);
  if (typeof file === 'object' && typeof file.url === 'string') return file.url;
  if (isHttpUrl(file)) return file;
  return doc400(file);
};

export default function DocPreview({ file }) {
  const [fileOpen, setFileOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const src = resolvePreviewSrc(file);
  useEffect(() => { setLoaded(false); }, [src]);

  if (!file) {
    return <Typography sx={{ ml: 1 }}>Copy not provided </Typography>;
  }

  const pdf = isPdf(src);

  const openExternal = () => {
    if (src) window.open(src, '_blank', 'noopener,noreferrer');
  };

  return (
    <div>
      <Box sx={{ my: 1 }}>
        {pdf ? (
          <Box>
            {/* Fixed-size stage so the loading state always fills the document
                area — the iframe sits inside it (never absolutely positioned
                out of flow), so the panel can't collapse to a skinny column. */}
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: { xs: '70vh', sm: 600 },
                borderRadius: '8px',
                overflow: 'hidden',
              }}
            >
              {!loaded && (
                <>
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1.5,
                      color: 'text.secondary',
                    }}
                  >
                    <CircularProgress size={28} />
                    <Typography variant="body2">Loading preview…</Typography>
                  </Box>
                </>
              )}
              <Box
                component="iframe"
                src={src}
                title="Document preview"
                onLoad={() => setLoaded(true)}
                sx={{
                  width: '100%',
                  height: '100%',
                  border: 0,
                  background: '#fff',
                  display: 'block',
                  transition: 'opacity 0.2s ease',
                  ...(!loaded && { opacity: 0 }),
                }}
              />
            </Box>
            {loaded && (
              <Button sx={{ mt: 1 }} fullWidth variant="outlined" onClick={openExternal}>
                Open PDF in new tab
              </Button>
            )}
          </Box>
        ) : (
          <Box sx={{ position: 'relative' }}>
            {!loaded && (
              <Skeleton
                variant="rectangular"
                sx={{ width: '100%', height: 300, borderRadius: '4px' }}
              />
            )}
            <Box
              component="img" loading="lazy"
              alt="Document preview"
              src={src}
              onLoad={() => setLoaded(true)}
              onClick={() => setFileOpen(true)}
              sx={{
                borderRadius: '4px',
                cursor: 'pointer',
                width: '100%',
                ...(!loaded && { position: 'absolute', opacity: 0 }),
              }}
            />
          </Box>
        )}
      </Box>

      {!pdf && (
        <LightboxModal
          images={[src]}
          photoIndex={0}
          setPhotoIndex={() => {}}
          isOpen={fileOpen}
          onClose={() => setFileOpen(false)}
        />
      )}
    </div>
  );
}

export const LoadingPreview = () => <Typography sx={{ my: 1 }}>loading preview...</Typography>;